import { useCallback } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { type OrderingContext, type UpsellRailSnapshot } from '@order/features-ordering';
import { useAppsFlyer } from '@order/integrations-appsflyer';
import {
  type TelemetryEventPayload,
  type TelemetrySelectedProduct,
  useTelemetry,
} from '@order/Telemetry';

import {
  formatTelemetryBag,
  formatBagForGoogleAnalytics,
  ORDER_PLAYBACK_CONFIG,
} from '../../utils';

/**
 * Telemetry events for bag/checkout.
 */
export const useBagTracking = () => {
  const params = useLocalSearchParams<BagScreenParams>();
  const { track } = useTelemetry();
  const { logInitiatedCheckoutEvent } = useAppsFlyer();

  const trackBagView = useCallback(
    (machineParams: { context: OrderingContext }) => {
      const { context } = machineParams;

      track('bag.view', { entryPoint: params?.referrer, ...formatTelemetryBag(context) });
    },
    [params?.referrer, track],
  );

  const trackViewCart = useCallback(
    (context: OrderingContext) => {
      track('ga.view_cart', formatBagForGoogleAnalytics(context));
    },
    [track],
  );

  const trackInitiatedCheckout = useCallback(
    (payload: InitiatedCheckoutPayload) => {
      logInitiatedCheckoutEvent(payload);
    },
    [logInitiatedCheckoutEvent],
  );

  const trackLocationNamePressed = useCallback(() => {
    track('bag.location-name.pressed');
  }, [track]);

  const trackWantedTimePressed = useCallback(() => {
    track('bag.wanted-time.pressed');
  }, [track]);

  const trackWantedTimeChanged = useCallback(() => {
    track('bag.wanted-time.changed');
  }, [track]);

  const trackDeliveryPreferencesPressed = useCallback(() => {
    track('bag.delivery-preferences.pressed');
  }, [track]);

  const trackDropoffLocationChanged = useCallback(() => {
    track('bag.dropoff-location.changed');
  }, [track]);

  const trackViewMenuPressed = useCallback(() => {
    track('bag.view-menu.pressed');
  }, [track]);

  const trackLineItemPressed = useCallback(() => {
    track('bag.line-item.pressed');
  }, [track]);

  const trackLineItemRemoved = useCallback(
    (selectedProduct: TelemetrySelectedProduct) => {
      track('bag.line-item.removed', { selectedProduct });
    },
    [track],
  );

  const trackLineItemQuantityChanged = useCallback(() => {
    track('bag.line-item.quantity.changed');
  }, [track]);

  const trackLoyaltyInfoPressed = useCallback(() => {
    track('bag.reward-info.pressed');
  }, [track]);

  const trackLoyaltyTermsPressed = useCallback(() => {
    track('bag.reward-terms.pressed');
  }, [track]);

  const trackLoyaltySignedOutPressed = useCallback(() => {
    track('bag.loyalty-signed-out.pressed');
  }, [track]);

  const trackApplyRewardPressed = useCallback(
    (reward: BagRewardEventPayload) => {
      track('bag.apply-reward.pressed', reward);
    },
    [track],
  );

  const trackRemoveRewardPressed = useCallback(
    (reward: BagRewardEventPayload) => {
      track('bag.remove-reward.pressed', reward);
    },
    [track],
  );

  const trackPromoCodeAddPressed = useCallback(() => {
    track('bag.promo-code.add.pressed');
  }, [track]);

  const trackUtensilsUsagePressed = useCallback(() => {
    track('bag.utensils-usage.pressed');
  }, [track]);

  const trackContinuePressed = useCallback(() => {
    track('bag.continue.pressed');
  }, [track]);

  const trackBeginCheckout = useCallback(
    (context: OrderingContext) => {
      track('ga.begin_checkout', formatBagForGoogleAnalytics(context));
    },
    [track],
  );

  const trackContinueDeliveryMinimumBlocked = useCallback(
    (minimum: string | number, amountNeeded: string | number) => {
      track('bag.continue.delivery-minimum-blocked', { minimum, amountNeeded });
    },
    [track],
  );

  const trackChangePaymentMethodPressed = useCallback(() => {
    track('bag.change-payment-method.pressed');
  }, [track]);

  const trackCreditPressed = useCallback(() => {
    track('bag.credit.pressed');
  }, [track]);

  const trackChangeFlexUsagePressed = useCallback(
    (toggled: boolean) => {
      track('bag.payment_type.toggled', { type: 'flex', toggled });
    },
    [track],
  );

  const trackPaymentMethodPressed = useCallback(() => {
    track('bag.payment-method.pressed');
  }, [track]);

  const trackRedeemGiftCardPressed = useCallback(() => {
    track('bag.redeem-gift-card.pressed');
  }, [track]);

  const trackRedeemGiftCardSubmitted = useCallback(() => {
    track('gift-card.redemption.submit', { source: 'bag' });
  }, [track]);

  const trackOrderPlaybackAutoSubmit = useCallback(() => {
    track('bag.order-playback.auto-submit', { orderPlaybackConfig: ORDER_PLAYBACK_CONFIG });
  }, [track]);

  const trackUpsellRailView = useCallback(
    (snapshot: UpsellRailSnapshot) => {
      track('bag_upsell.rail.view', snapshot);
    },
    [track],
  );

  const trackOrderPlaybackGoBack = useCallback(() => {
    track('bag.order-playback.go-back', { orderPlaybackConfig: ORDER_PLAYBACK_CONFIG });
  }, [track]);

  return {
    trackBagView,
    trackViewCart,
    trackUpsellRailView,
    trackInitiatedCheckout,
    trackLocationNamePressed,
    trackWantedTimePressed,
    trackWantedTimeChanged,
    trackDeliveryPreferencesPressed,
    trackDropoffLocationChanged,
    trackViewMenuPressed,
    trackLineItemPressed,
    trackLineItemRemoved,
    trackLineItemQuantityChanged,
    trackLoyaltyInfoPressed,
    trackLoyaltyTermsPressed,
    trackLoyaltySignedOutPressed,
    trackApplyRewardPressed,
    trackRemoveRewardPressed,
    trackPromoCodeAddPressed,
    trackUtensilsUsagePressed,
    trackContinuePressed,
    trackBeginCheckout,
    trackContinueDeliveryMinimumBlocked,
    trackChangePaymentMethodPressed,
    trackCreditPressed,
    trackChangeFlexUsagePressed,
    trackPaymentMethodPressed,
    trackRedeemGiftCardPressed,
    trackRedeemGiftCardSubmitted,
    trackOrderPlaybackAutoSubmit,
    trackOrderPlaybackGoBack,
  };
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagScreenParams = {
  referrer?: string;
};

type InitiatedCheckoutPayload = {
  price: number;
  productIds: string;
  orderType: string;
  quantity: number;
};

type BagRewardEventPayload = TelemetryEventPayload<'bag.apply-reward.pressed'>;
